// ============================================================
// Phexotial — Extension Preferences
// ============================================================
// Small extension-specific preferences stored in
// chrome.storage.sync (non-sensitive, cross-device).
// ============================================================

import type { CaptureDestination } from './capture';

const PREFS_KEY = 'phexotial-extension-prefs';

export interface ExtensionPrefs {
  defaultCaptureDestination: CaptureDestination;
  closePopupOnCapture: boolean;
  showCaptureConfirmation: boolean;
  linkOpeningPreference: 'new-tab' | 'current-tab';
  showUpdateMessages: boolean;
  websiteFallbackUrl: string;
  contextMenuEnabled: boolean;
}

export const DEFAULT_EXTENSION_PREFS: ExtensionPrefs = {
  defaultCaptureDestination: 'reading-queue',
  closePopupOnCapture: false,
  showCaptureConfirmation: true,
  linkOpeningPreference: 'new-tab',
  showUpdateMessages: true,
  websiteFallbackUrl: 'https://phexotial.com',
  contextMenuEnabled: true,
};

export async function getExtensionPrefs(): Promise<ExtensionPrefs> {
  try {
    if (typeof chrome !== 'undefined' && chrome.storage?.sync?.get) {
      const result = await chrome.storage.sync.get(PREFS_KEY);
      const stored = result[PREFS_KEY] as Partial<ExtensionPrefs> | undefined;
      if (stored) {
        return { ...DEFAULT_EXTENSION_PREFS, ...stored };
      }
    }
  } catch {
    // storage API unavailable
  }
  return { ...DEFAULT_EXTENSION_PREFS };
}

export async function setExtensionPrefs(prefs: Partial<ExtensionPrefs>): Promise<void> {
  try {
    if (typeof chrome !== 'undefined' && chrome.storage?.sync?.set) {
      const current = await getExtensionPrefs();
      const merged = { ...current, ...prefs };
      await chrome.storage.sync.set({ [PREFS_KEY]: merged });
    }
  } catch {
    // storage API unavailable
  }
}

export async function resetExtensionPrefs(): Promise<void> {
  try {
    if (typeof chrome !== 'undefined' && chrome.storage?.sync?.set) {
      await chrome.storage.sync.set({ [PREFS_KEY]: DEFAULT_EXTENSION_PREFS });
    }
  } catch {
    // storage API unavailable
  }
}

// --- Diagnostics (local storage, device-specific) ---

const DIAG_KEY = 'phexotial-diagnostics';

export interface ExtensionDiagnostics {
  version: string;
  installDate: string | null;
  lastUpdate: string | null;
  lastAcknowledgedVersion: string | null;
  captureCount: number;
}

export async function getDiagnostics(): Promise<ExtensionDiagnostics> {
  try {
    if (typeof chrome !== 'undefined' && chrome.storage?.local?.get) {
      const result = await chrome.storage.local.get(DIAG_KEY);
      const stored = result[DIAG_KEY] as Partial<ExtensionDiagnostics> | undefined;
      if (stored) {
        return { ...stored } as ExtensionDiagnostics;
      }
    }
  } catch {
    // storage API unavailable
  }
  return {
    version: '1.2.0',
    installDate: null,
    lastUpdate: null,
    lastAcknowledgedVersion: null,
    captureCount: 0,
  };
}

export async function setDiagnostics(diag: Partial<ExtensionDiagnostics>): Promise<void> {
  try {
    if (typeof chrome !== 'undefined' && chrome.storage?.local?.set) {
      const current = await getDiagnostics();
      const merged = { ...current, ...diag };
      await chrome.storage.local.set({ [DIAG_KEY]: merged });
    }
  } catch {
    // storage API unavailable
  }
}

export async function incrementCaptureCount(): Promise<void> {
  const diag = await getDiagnostics();
  await setDiagnostics({ captureCount: diag.captureCount + 1 });
}
